import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Input, Select, Badge, cn } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api, supabase } from '../../lib/api';
import { compressImage, IMAGE_COMPRESS_PRESETS } from '../../lib/compressImage';
import ChannelSelector from '../../components/Leads/ChannelSelector';
import CopyButton from '../../components/ui/CopyButton';
import SafeIcon from '../../common/SafeIcon';
import Spinner from '../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';

const {
  FiUser, FiSave, FiCheck, FiAward, FiStar, FiShare2, FiLink, FiX, FiPlus,
  FiGlobe, FiExternalLink, FiCalendar, FiUpload,
} = FiIcons;

const HEADSHOT_BUCKET = 'agent-headshots';
const HEADSHOT_MAX_BYTES = 5 * 1024 * 1024;
const HEADSHOT_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';

function headshotExtFromFile(file) {
  const fromName = String(file?.name || '').split('.').pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{1,5}$/.test(fromName)) return fromName;
  const mime = String(file?.type || '').toLowerCase();
  if (mime === 'image/jpeg' || mime === 'image/jpg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/gif') return 'gif';
  return 'jpg';
}

const CALENDAR_MODE_OPTIONS = [
  {
    id: 'crm',
    label: 'Use CRM calendar',
    description: 'Self-serve booking on your public page using Google Calendar + availability from Settings → Calendar.',
  },
  {
    id: 'link',
    label: 'Link to calendar',
    description: 'Send prospects to an external booking URL (Calendly, etc.).',
  },
  {
    id: 'embed',
    label: 'Embed calendar',
    description: 'Paste embed HTML from Calendly or similar to render on your public page.',
  },
  {
    id: 'none',
    label: 'No calendar',
    description: 'Hide all booking CTAs on your public profile.',
  },
];

const PUBLIC_PROFILE_BASE = 'https://prosperityleaders.net';

const RESERVED_SLUGS = new Set([
  'admin', 'agent', 'agents', 'api', 'blog', 'contact', 'dashboard', 'go',
  'home', 'login', 'logout', 'signup', 'register', 'opportunity',
  'privacy-policy', 'terms-of-service', 'services', 'about', 'join', 'r',
  'redirects', 'settings', 'profile', 'support', 'find-a-professional',
]);

const RESOURCE_TYPE_OPTIONS = [
  { value: 'link', label: 'Link' },
  { value: 'pdf', label: 'PDF' },
  { value: 'video', label: 'Video' },
  { value: 'blog', label: 'Blog' },
];

function isValidHttpUrl(url) {
  return /^https?:\/\/.+/i.test(String(url || '').trim());
}

function emptyResource() {
  return { type: 'link', title: '', url: '' };
}

function StarDisplay({ rating }) {
  const n = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-500" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <SafeIcon
          key={i}
          icon={FiStar}
          className={`w-3.5 h-3.5 ${i < n ? 'fill-current' : 'text-gray-300'}`}
        />
      ))}
    </span>
  );
}

function testimonialStatusBadge(status) {
  switch (status) {
    case 'approved':
      return { variant: 'success', label: 'Approved' };
    case 'rejected':
      return { variant: 'danger', label: 'Rejected' };
    case 'pending':
    default:
      return { variant: 'warning', label: 'Pending' };
  }
}

/** Same pattern as Settings tabs — Kit has no Switch export. */
function ToggleSwitch({ checked, onChange, disabled, id, 'aria-labelledby': ariaLabelledby }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={ariaLabelledby}
      id={id}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

const DIALER_OPTIONS = [
  {
    id: 'twilio',
    label: 'Twilio Voice',
    description:
      'Browser dialer powered by the Twilio Voice SDK. Requires the twilio-token Edge Function and a Twilio TwiML application for outbound calls.',
  },
  {
    id: 'telnyx',
    label: 'Telnyx WebRTC',
    description:
      'Browser dialer powered by the Telnyx WebRTC SDK. Uses a credential_connection-bound login_token from the telnyx-token Edge Function.',
  },
];

function emptyProfile() {
  return {
    full_name: '',
    title: '',
    email: '',
    phone_number: '',
    headshot_url: '',
    bio: '',
    preferred_quote: '',
    best_income_milestone: '',
    years_in_industry: '',
    clients_served: '',
    designations: [],
    awards: [],
    skills: [],
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    youtube_url: '',
    tiktok_url: '',
    personal_booking_url: '',
    personal_cta_text: '',
    calendar_mode: 'crm',
    calendar_embed_code: '',
    preferred_dialer: 'twilio',
    default_channel: null,
  };
}

function profileFromUser(user) {
  if (!user) return emptyProfile();
  return {
    full_name: user.full_name || '',
    title: user.title || '',
    email: user.email || '',
    phone_number: user.phone_number || '',
    headshot_url: user.headshot_url || '',
    bio: user.bio || '',
    preferred_quote: user.preferred_quote || '',
    best_income_milestone: user.best_income_milestone || '',
    years_in_industry: user.years_in_industry ?? '',
    clients_served: user.clients_served ?? '',
    designations: Array.isArray(user.designations) ? [...user.designations] : [],
    awards: Array.isArray(user.awards) ? user.awards.map((a) => ({ ...a })) : [],
    skills: Array.isArray(user.skills) ? [...user.skills] : [],
    facebook_url: user.facebook_url || '',
    instagram_url: user.instagram_url || '',
    linkedin_url: user.linkedin_url || '',
    youtube_url: user.youtube_url || '',
    tiktok_url: user.tiktok_url || '',
    personal_booking_url: user.personal_booking_url || '',
    personal_cta_text: user.personal_cta_text || '',
    calendar_mode: ['crm', 'link', 'embed', 'none'].includes(user.calendar_mode)
      ? user.calendar_mode
      : 'crm',
    calendar_embed_code: user.calendar_embed_code || '',
    preferred_dialer:
      user.preferred_dialer === 'telnyx' || user.preferred_dialer === 'twilio'
        ? user.preferred_dialer
        : 'twilio',
    default_channel: user.default_channel || null,
    office_address: user.office_address || '',
  };
}

function profilePayload(form) {
  return {
    full_name: form.full_name.trim() || null,
    title: form.title.trim() || null,
    phone_number: form.phone_number.trim() || null,
    headshot_url: form.headshot_url.trim() || null,
    bio: form.bio.trim() || null,
    preferred_quote: form.preferred_quote.trim() || null,
    best_income_milestone: form.best_income_milestone.trim() || null,
    years_in_industry:
      form.years_in_industry === '' || form.years_in_industry == null
        ? null
        : Number(form.years_in_industry),
    clients_served:
      form.clients_served === '' || form.clients_served == null
        ? null
        : Number(form.clients_served),
    designations: form.designations.length ? form.designations : null,
    awards: form.awards,
    skills: form.skills.length ? form.skills : null,
    facebook_url: form.facebook_url.trim() || null,
    instagram_url: form.instagram_url.trim() || null,
    linkedin_url: form.linkedin_url.trim() || null,
    youtube_url: form.youtube_url.trim() || null,
    tiktok_url: form.tiktok_url.trim() || null,
    personal_booking_url: form.personal_booking_url.trim() || null,
    personal_cta_text: form.personal_cta_text.trim() || null,
    calendar_mode: form.calendar_mode || 'crm',
    calendar_embed_code:
      form.calendar_mode === 'embed' ? form.calendar_embed_code.trim() || null : form.calendar_embed_code.trim() || null,
    preferred_dialer: form.preferred_dialer,
    default_channel: form.default_channel,
    office_address: form.office_address.trim() || null,
  };
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-2 border-b border-gray-100 pb-3">
      {icon && <SafeIcon icon={icon} className="w-4 h-4 text-gray-500 mt-0.5" />}
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function TagInput({ label, value, onChange, placeholder, disabled }) {
  const [draft, setDraft] = useState('');

  const addTag = () => {
    const tag = draft.trim();
    if (!tag || value.includes(tag)) {
      setDraft('');
      return;
    }
    onChange([...value, tag]);
    setDraft('');
  };

  const removeTag = (tag) => onChange(value.filter((t) => t !== tag));

  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium"
          >
            {tag}
            <button
              type="button"
              disabled={disabled}
              onClick={() => removeTag(tag)}
              className="hover:text-primary-dark disabled:opacity-50"
              aria-label={`Remove ${tag}`}
            >
              <SafeIcon icon={FiX} className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag();
            }
          }}
          disabled={disabled}
          placeholder={placeholder || 'Type and press Enter'}
          className="flex h-10 flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        />
        <Button type="button" variant="outline" size="sm" disabled={disabled || !draft.trim()} onClick={addTag}>
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function slugStatusMessage(status) {
  switch (status) {
    case 'checking':
      return { text: 'Checking availability…', className: 'text-gray-500' };
    case 'available':
      return { text: 'Slug is available', className: 'text-green-600' };
    case 'taken':
      return { text: 'That slug is already taken', className: 'text-red-600' };
    case 'invalid':
      return {
        text: 'Use lowercase letters, numbers, and hyphens only (1–50 chars, no leading/trailing hyphen)',
        className: 'text-red-600',
      };
    case 'reserved':
      return { text: 'That slug is reserved for the site', className: 'text-red-600' };
    default:
      return null;
  }
}

function PublicProfileSection() {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(null);
  const [changingSlug, setChangingSlug] = useState(false);
  const [slugInput, setSlugInput] = useState('');
  const [slugStatus, setSlugStatus] = useState('idle');
  const [claiming, setClaiming] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [resources, setResources] = useState([]);
  const [resourceErrors, setResourceErrors] = useState([]);
  const [savingResources, setSavingResources] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);
  const [moderatingId, setModeratingId] = useState(null);
  const debounceRef = useRef(null);
  const checkSeqRef = useRef(0);

  const loadTestimonials = useCallback(async () => {
    setLoadingTestimonials(true);
    try {
      const rows = await api.agentTestimonials.getForMyPage();
      setTestimonials(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('[Profile] failed to load testimonials:', e);
      addToast({
        type: 'error',
        title: 'Could not load testimonials',
        message: e?.message || 'Please try again.',
      });
    } finally {
      setLoadingTestimonials(false);
    }
  }, [addToast]);

  const loadMine = useCallback(async () => {
    try {
      const row = await api.agentPublicPages.getMine();
      setPage(row);
      setResources(
        Array.isArray(row?.resources)
          ? row.resources.map((r) => ({
              type: r?.type || 'link',
              title: r?.title || '',
              url: r?.url || '',
            }))
          : [],
      );
      if (row) setChangingSlug(false);
      if (row?.id) {
        void loadTestimonials();
      } else {
        setTestimonials([]);
      }
    } catch (e) {
      console.error('[Profile] failed to load public page:', e);
      addToast({
        type: 'error',
        title: 'Could not load public profile',
        message: e?.message || 'Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, loadTestimonials]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const runSlugCheck = useCallback(async (raw) => {
    const slug = String(raw ?? '').trim().toLowerCase();
    if (!slug) {
      setSlugStatus('idle');
      return;
    }
    if (!api.agentPublicPages.isValidSlug(slug)) {
      setSlugStatus('invalid');
      return;
    }
    if (RESERVED_SLUGS.has(slug)) {
      setSlugStatus('reserved');
      return;
    }
    if (page?.slug && slug === page.slug) {
      setSlugStatus('available');
      return;
    }
    const seq = ++checkSeqRef.current;
    setSlugStatus('checking');
    try {
      const available = await api.agentPublicPages.checkSlugAvailable(slug);
      if (seq !== checkSeqRef.current) return;
      setSlugStatus(available ? 'available' : 'taken');
    } catch (e) {
      if (seq !== checkSeqRef.current) return;
      setSlugStatus('idle');
      addToast({
        type: 'error',
        title: 'Could not check slug',
        message: e?.message || 'Please try again.',
      });
    }
  }, [addToast, page?.slug]);

  const onSlugChange = (value) => {
    const next = String(value ?? '').toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlugInput(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runSlugCheck(next);
    }, 500);
  };

  const handleClaimOrUpdateSlug = async () => {
    const slug = slugInput.trim().toLowerCase();
    if (!api.agentPublicPages.isValidSlug(slug)) {
      setSlugStatus('invalid');
      return;
    }
    if (RESERVED_SLUGS.has(slug)) {
      setSlugStatus('reserved');
      return;
    }
    if (slugStatus === 'taken' || slugStatus === 'checking') return;

    setClaiming(true);
    try {
      const available = await api.agentPublicPages.checkSlugAvailable(slug);
      if (!available && slug !== page?.slug) {
        setSlugStatus('taken');
        return;
      }
      if (page?.id) {
        const updated = await api.agentPublicPages.update(page.id, { slug });
        setPage(updated);
        setChangingSlug(false);
        setSlugInput('');
        setSlugStatus('idle');
        addToast({ type: 'success', title: 'Slug updated', message: `Your link is now ${PUBLIC_PROFILE_BASE}/${updated.slug}` });
      } else {
        const created = await api.agentPublicPages.create({ slug });
        setPage(created);
        setResources([]);
        setSlugInput('');
        setSlugStatus('idle');
        void loadTestimonials();
        addToast({
          type: 'success',
          title: 'Slug claimed',
          message: 'Your public profile is ready. Toggle it live when you want it published.',
        });
      }
    } catch (e) {
      const message = api.agentPublicPages.translateError(e);
      if (/taken|already/i.test(message)) setSlugStatus('taken');
      else if (/reserved/i.test(message)) setSlugStatus('reserved');
      else if (/lowercase|format|hyphen/i.test(message)) setSlugStatus('invalid');
      addToast({ type: 'error', title: 'Could not save slug', message });
    } finally {
      setClaiming(false);
    }
  };

  const handleToggleLive = async (next) => {
    if (!page?.id) return;
    setToggling(true);
    const prev = page.is_public;
    setPage((p) => (p ? { ...p, is_public: next } : p));
    try {
      const updated = await api.agentPublicPages.update(page.id, { is_public: next });
      setPage(updated);
      addToast({
        type: 'success',
        title: next ? 'Profile is live' : 'Profile unpublished',
        message: next
          ? `Visible at ${PUBLIC_PROFILE_BASE}/${updated.slug}`
          : 'Your public profile is no longer publicly listed.',
      });
    } catch (e) {
      setPage((p) => (p ? { ...p, is_public: prev } : p));
      addToast({
        type: 'error',
        title: 'Could not update visibility',
        message: api.agentPublicPages.translateError(e),
      });
    } finally {
      setToggling(false);
    }
  };

  const updateResource = (index, field, value) => {
    setResources((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    setResourceErrors((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const validateResources = (list) => {
    const errors = list.map((r) => {
      const title = String(r.title || '').trim();
      const url = String(r.url || '').trim();
      if (!title || !url) return 'Title and URL are required.';
      if (!isValidHttpUrl(url)) return 'URL must start with http:// or https://';
      return null;
    });
    setResourceErrors(errors);
    return errors.every((e) => !e);
  };

  const handleSaveResources = async () => {
    if (!page?.id) return;
    if (!validateResources(resources)) {
      addToast({
        type: 'error',
        title: 'Fix resource fields',
        message: 'Each resource needs a title and a valid http(s) URL.',
      });
      return;
    }
    setSavingResources(true);
    try {
      const payload = resources.map((r) => ({
        type: r.type || 'link',
        title: String(r.title).trim(),
        url: String(r.url).trim(),
      }));
      const updated = await api.agentPublicPages.update(page.id, { resources: payload });
      setPage(updated);
      setResources(
        Array.isArray(updated.resources)
          ? updated.resources.map((r) => ({
              type: r?.type || 'link',
              title: r?.title || '',
              url: r?.url || '',
            }))
          : [],
      );
      addToast({ type: 'success', title: 'Resources saved', message: 'Your public profile resources were updated.' });
    } catch (e) {
      addToast({
        type: 'error',
        title: 'Could not save resources',
        message: api.agentPublicPages.translateError(e),
      });
    } finally {
      setSavingResources(false);
    }
  };

  const handleModerateTestimonial = async (id, status) => {
    setModeratingId(id);
    try {
      await api.agentTestimonials.updateStatus(id, status);
      addToast({
        type: 'success',
        title: status === 'approved' ? 'Testimonial approved' : 'Testimonial rejected',
        message:
          status === 'approved'
            ? 'It will appear on your public profile.'
            : 'It will not be shown publicly.',
      });
      await loadTestimonials();
    } catch (e) {
      addToast({
        type: 'error',
        title: 'Could not update testimonial',
        message: e?.message || 'Please try again.',
      });
    } finally {
      setModeratingId(null);
    }
  };

  const statusInfo = slugStatusMessage(slugStatus);
  const showSlugForm = !page || changingSlug;
  const liveUrl = page?.slug ? `${PUBLIC_PROFILE_BASE}/${page.slug}` : '';

  return (
    <Card className="p-6 space-y-5">
      <SectionHeader
        icon={FiGlobe}
        title="Public Profile Page"
        subtitle="Claim a public link and share resources on prosperityleaders.net."
      />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
          <Spinner className="text-primary" />
          Loading public profile…
        </div>
      ) : (
        <>
          {showSlugForm ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Claim your public profile link, e.g. prosperityleaders.net/yourname
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1">
                  <Input
                    label="Profile slug"
                    value={slugInput}
                    onChange={(e) => onSlugChange(e.target.value)}
                    disabled={claiming}
                    placeholder="yourname"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <Button
                  type="button"
                  disabled={
                    claiming
                    || !slugInput.trim()
                    || slugStatus === 'checking'
                    || slugStatus === 'taken'
                    || slugStatus === 'invalid'
                    || slugStatus === 'reserved'
                    || (page?.slug && slugInput.trim() === page.slug)
                  }
                  onClick={() => void handleClaimOrUpdateSlug()}
                >
                  {claiming ? (
                    <>
                      <Spinner className="mr-2" />
                      {page ? 'Updating…' : 'Claiming…'}
                    </>
                  ) : page ? (
                    'Update slug'
                  ) : (
                    'Claim'
                  )}
                </Button>
                {page && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={claiming}
                    onClick={() => {
                      setChangingSlug(false);
                      setSlugInput('');
                      setSlugStatus('idle');
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
              {statusInfo && (
                <p className={`text-sm flex items-center gap-1.5 ${statusInfo.className}`}>
                  {slugStatus === 'available' && <SafeIcon icon={FiCheck} className="w-4 h-4" />}
                  {slugStatus === 'checking' && <Spinner className="w-3.5 h-3.5" />}
                  {statusInfo.text}
                </p>
              )}
              <p className="text-xs text-gray-400">
                Lowercase letters, numbers, and hyphens only. Preview: {PUBLIC_PROFILE_BASE}/
                {slugInput || 'yourname'}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Your slug</p>
                  <p className="text-sm text-gray-900 font-mono mt-0.5">{page.slug}</p>
                </div>
                <button
                  type="button"
                  className="text-sm text-primary hover:text-primary-dark"
                  onClick={() => {
                    setChangingSlug(true);
                    setSlugInput(page.slug || '');
                    setSlugStatus('available');
                  }}
                >
                  Change slug
                </button>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-md border border-gray-200 bg-gray-50/60 px-4 py-3">
                <div>
                  <p id="profile-live-label" className="text-sm font-medium text-gray-900">
                    Profile is Live
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When on, your page is publicly reachable at your slug.
                  </p>
                </div>
                <ToggleSwitch
                  id="profile-is-live"
                  checked={Boolean(page.is_public)}
                  onChange={(v) => void handleToggleLive(v)}
                  disabled={toggling}
                  aria-labelledby="profile-live-label"
                />
              </div>

              {page.is_public && (
                <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-green-700 flex-shrink-0" />
                  <a
                    href={liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-800 hover:underline truncate"
                  >
                    {liveUrl}
                  </a>
                  <CopyButton text={liveUrl} title="Copy public profile URL" className="text-green-700" />
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Resources</p>
                    <p className="text-xs text-gray-500 mt-0.5">Up to 3 links shown on your public page.</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={resources.length >= 3 || savingResources}
                    onClick={() => setResources((prev) => [...prev, emptyResource()])}
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                {resources.length === 0 && (
                  <p className="text-sm text-gray-500">No resources yet. Add up to 3.</p>
                )}

                <div className="space-y-3">
                  {resources.map((resource, index) => (
                    <div
                      key={index}
                      className="rounded-md border border-gray-200 p-3 bg-gray-50/50 space-y-2"
                    >
                      <div className="flex gap-2 items-start">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <Select
                            label="Type"
                            options={RESOURCE_TYPE_OPTIONS}
                            value={resource.type}
                            onChange={(e) => updateResource(index, 'type', e.target.value)}
                            disabled={savingResources}
                          />
                          <Input
                            label="Title"
                            value={resource.title}
                            onChange={(e) => updateResource(index, 'title', e.target.value)}
                            disabled={savingResources}
                            placeholder="Resource title"
                          />
                          <Input
                            label="URL"
                            value={resource.url}
                            onChange={(e) => updateResource(index, 'url', e.target.value)}
                            disabled={savingResources}
                            placeholder="https://…"
                          />
                        </div>
                        <button
                          type="button"
                          disabled={savingResources}
                          onClick={() => {
                            setResources((prev) => prev.filter((_, i) => i !== index));
                            setResourceErrors((prev) => prev.filter((_, i) => i !== index));
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 mt-6"
                          aria-label="Remove resource"
                        >
                          <SafeIcon icon={FiX} className="w-4 h-4" />
                        </button>
                      </div>
                      {resourceErrors[index] && (
                        <p className="text-sm text-red-600">{resourceErrors[index]}</p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    disabled={savingResources}
                    onClick={() => void handleSaveResources()}
                  >
                    {savingResources ? (
                      <>
                        <Spinner className="mr-2" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiSave} className="mr-2" />
                        Save resources
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-3 border-t border-gray-200 pt-5">
                <div>
                  <p className="text-sm font-medium text-gray-900">Testimonials</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Review submissions from your public profile. Only approved ones are shown publicly.
                  </p>
                </div>

                {loadingTestimonials ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Spinner className="text-primary" />
                    Loading testimonials…
                  </div>
                ) : testimonials.length === 0 ? (
                  <p className="text-sm text-gray-500">No testimonials yet.</p>
                ) : (
                  <div className="space-y-3">
                    {testimonials.map((t) => {
                      const badge = testimonialStatusBadge(t.status);
                      const showStars = Number(t.rating) >= 4;
                      const busy = moderatingId === t.id;
                      return (
                        <div
                          key={t.id}
                          className="rounded-md border border-gray-200 p-3 bg-gray-50/50 space-y-2"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0 space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {t.author_name}
                                </p>
                                <Badge variant={badge.variant}>{badge.label}</Badge>
                                {showStars && <StarDisplay rating={t.rating} />}
                              </div>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{t.body}</p>
                            </div>
                            {t.status === 'pending' && (
                              <div className="flex items-center gap-2 shrink-0">
                                <Button
                                  type="button"
                                  size="sm"
                                  disabled={busy}
                                  onClick={() => void handleModerateTestimonial(t.id, 'approved')}
                                >
                                  {busy ? <Spinner className="w-3.5 h-3.5" /> : 'Approve'}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={busy}
                                  onClick={() => void handleModerateTestimonial(t.id, 'rejected')}
                                >
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

const PROFILE_TABS = [
  { id: 'personal', label: 'Personal Info' },
  { id: 'public', label: 'Public Profile' },
  { id: 'booking', label: 'Booking & CTA' },
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyProfile());
  const [savedForm, setSavedForm] = useState(emptyProfile());
  const [activeTab, setActiveTab] = useState('personal');
  const [googleConnected, setGoogleConnected] = useState(null);
  const [headshotUploading, setHeadshotUploading] = useState(false);
  const [headshotUploadError, setHeadshotUploadError] = useState('');
  const [localHeadshotPreview, setLocalHeadshotPreview] = useState(null);
  const headshotFileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fresh = await api.auth.getCurrentUser();
        if (cancelled || !fresh) return;
        const next = profileFromUser(fresh);
        setForm(next);
        setSavedForm(next);
        setUser((prev) => (prev ? { ...prev, ...fresh } : fresh));
      } catch (e) {
        console.error('[Profile] failed to refresh user:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await api.agentCalendarSettings.get();
        if (!cancelled) setGoogleConnected(Boolean(settings?.google_connected));
      } catch {
        if (!cancelled) setGoogleConnected(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    return () => {
      if (localHeadshotPreview) URL.revokeObjectURL(localHeadshotPreview);
    };
  }, [localHeadshotPreview]);

  const handleHeadshotUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setHeadshotUploadError('');
    if (!file) return;

    if (!user?.id) {
      setHeadshotUploadError('You must be signed in to upload a headshot.');
      return;
    }
    if (!supabase) {
      setHeadshotUploadError('Storage is unavailable in this environment.');
      return;
    }
    if (!String(file.type || '').startsWith('image/')) {
      setHeadshotUploadError('Please choose an image file (JPEG, PNG, WebP, or GIF).');
      return;
    }
    if (file.size > HEADSHOT_MAX_BYTES) {
      setHeadshotUploadError('Image must be 5 MB or smaller.');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setLocalHeadshotPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return previewUrl;
    });

    setHeadshotUploading(true);
    try {
      const uploadFile = await compressImage(file, IMAGE_COMPRESS_PRESETS.headshot);
      const ext = headshotExtFromFile(uploadFile);
      const path = `${user.id}/headshot-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from(HEADSHOT_BUCKET)
        .upload(path, uploadFile, {
          cacheControl: '31536000',
          upsert: false,
          contentType: uploadFile.type || `image/${ext}`,
        });
      if (uploadError) throw uploadError;

      const { data: publicData } = supabase.storage.from(HEADSHOT_BUCKET).getPublicUrl(path);
      const publicUrl = publicData?.publicUrl;
      if (!publicUrl) throw new Error('Upload succeeded but no public URL was returned.');

      setForm((prev) => ({ ...prev, headshot_url: publicUrl }));
      setLocalHeadshotPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      addToast({
        type: 'success',
        title: 'Headshot uploaded',
        message: 'URL field updated — save your profile to keep it.',
      });
    } catch (err) {
      console.error('[Profile] headshot upload failed:', err);
      setHeadshotUploadError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setHeadshotUploading(false);
    }
  };

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  const handleSave = async () => {
    if (!user?.id) {
      addToast({ type: 'error', title: 'Cannot save', message: 'No user id on profile.' });
      return;
    }
    setSaving(true);
    try {
      const updated = await api.users.updateProfile(user.id, profilePayload(form));
      const next = profileFromUser(updated || { ...user, ...profilePayload(form) });
      setForm(next);
      setSavedForm(next);
      setUser((prev) => (prev ? { ...prev, ...(updated || profilePayload(form)) } : prev));
      addToast({ type: 'success', title: 'Profile saved', message: 'Your profile has been updated.' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not save profile',
        message: err?.message || 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const updateAwards = (awards) => setForm((prev) => ({ ...prev, awards }));

  return (
    <div className="space-y-6 max-w-3xl pb-8">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-lg">
          <SafeIcon icon={FiUser} className="h-6 w-6 text-gray-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-sm text-gray-500">
            Manage your public agent profile for funnels and recruiting pages.
          </p>
        </div>
      </div>

      {loading ? (
        <Card className="p-8 flex items-center justify-center gap-2 text-sm text-gray-500">
          <Spinner className="text-primary" />
          Loading profile…
        </Card>
      ) : (
        <>
          <div className="border-b border-gray-200 bg-white rounded-t-lg overflow-x-auto">
            <nav className="flex gap-0 min-w-max md:min-w-0" aria-label="Profile sections">
              {PROFILE_TABS.map(({ id: tab, label }) => (
                <button
                  key={tab}
                  type="button"
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setActiveTab(tab)}
                  aria-current={activeTab === tab ? 'page' : undefined}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {/* Keep all tab panels mounted (hidden) so PublicProfileSection state and form dirty tracking survive tab switches. */}
          <div className={activeTab === 'personal' ? 'space-y-6' : 'hidden'}>
            <Card className="p-6 space-y-5">
              <SectionHeader
                icon={FiUser}
                title="Personal Info"
                subtitle="Basic details shown on your agent profile."
              />
              <Input label="Name" value={form.full_name} onChange={setField('full_name')} disabled={saving} />
              <Input label="Title" value={form.title} onChange={setField('title')} disabled={saving} placeholder="Agency Owner, Senior Agent…" />
              <Input label="Email" value={form.email} disabled readOnly />
              <Input label="Phone" value={form.phone_number} onChange={setField('phone_number')} disabled={saving} />
              <div>
                <Input
                  label="Office Address"
                  value={form.office_address}
                  onChange={setField('office_address')}
                  disabled={saving}
                  placeholder="632 Newark Avenue, Jersey City NJ 07306. Suite 212."
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Default location for in-person appointments. If left blank, bookings fall back to
                  632 Newark Avenue, Jersey City NJ 07306. Suite 212.
                </p>
              </div>
              <div className="space-y-3">
                <Input
                  label="Headshot URL"
                  value={form.headshot_url}
                  onChange={(e) => {
                    setHeadshotUploadError('');
                    setField('headshot_url')(e);
                  }}
                  disabled={saving || headshotUploading}
                  placeholder="https://…"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    ref={headshotFileInputRef}
                    type="file"
                    accept={HEADSHOT_ACCEPT}
                    className="sr-only"
                    onChange={handleHeadshotUpload}
                    disabled={saving || headshotUploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={saving || headshotUploading || !user?.id}
                    isLoading={headshotUploading}
                    onClick={() => headshotFileInputRef.current?.click()}
                  >
                    <SafeIcon icon={FiUpload} className="w-3.5 h-3.5 mr-1.5" />
                    {headshotUploading ? 'Uploading…' : 'Upload Image'}
                  </Button>
                  <p className="text-xs text-gray-500">
                    JPEG, PNG, WebP, or GIF · max 5 MB. Upload fills the URL field above.
                  </p>
                </div>
                {headshotUploadError && (
                  <p className="text-sm text-red-500" role="alert">{headshotUploadError}</p>
                )}
                {(localHeadshotPreview || form.headshot_url) && (
                  <div className="flex items-center gap-4">
                    <img
                      src={localHeadshotPreview || form.headshot_url}
                      alt="Headshot preview"
                      width={80}
                      height={80}
                      loading="lazy"
                      decoding="async"
                      className="h-20 w-20 rounded-full object-cover border border-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <p className="text-xs text-gray-500">
                      {headshotUploading ? 'Uploading preview…' : 'Headshot preview'}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={form.bio}
                  onChange={setField('bio')}
                  disabled={saving}
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary disabled:opacity-50"
                  placeholder="Tell prospects about your background and approach…"
                />
              </div>
              <Input label="Preferred Quote" value={form.preferred_quote} onChange={setField('preferred_quote')} disabled={saving} placeholder="A quote that represents your philosophy…" />
            </Card>

            <Card className="p-6 space-y-5">
              <SectionHeader
                icon={FiStar}
                title="Skills & Qualities"
                subtitle="Highlight strengths like you would on LinkedIn."
              />
              <TagInput
                label="Skills"
                value={form.skills}
                onChange={(skills) => setForm((prev) => ({ ...prev, skills }))}
                placeholder="e.g. Medicare, IUL — press Enter to add"
                disabled={saving}
              />
            </Card>

            <Card className="p-6 space-y-5">
              <SectionHeader
                icon={FiShare2}
                title="Social Media Links"
                subtitle="Optional links displayed on your public profile."
              />
              <Input label="Facebook URL" value={form.facebook_url} onChange={setField('facebook_url')} disabled={saving} placeholder="https://facebook.com/…" />
              <Input label="Instagram URL" value={form.instagram_url} onChange={setField('instagram_url')} disabled={saving} placeholder="https://instagram.com/…" />
              <Input label="LinkedIn URL" value={form.linkedin_url} onChange={setField('linkedin_url')} disabled={saving} placeholder="https://linkedin.com/in/…" />
              <Input label="YouTube URL" value={form.youtube_url} onChange={setField('youtube_url')} disabled={saving} placeholder="https://youtube.com/…" />
              <Input label="TikTok URL" value={form.tiktok_url} onChange={setField('tiktok_url')} disabled={saving} placeholder="https://tiktok.com/@…" />
            </Card>
          </div>

          <div className={activeTab === 'public' ? 'space-y-6' : 'hidden'}>
            <PublicProfileSection />

            <Card className="p-6 space-y-5">
              <SectionHeader
                icon={FiAward}
                title="Success Items"
                subtitle="Credentials and results that build trust."
              />
              <Input label="Best Income Milestone" value={form.best_income_milestone} onChange={setField('best_income_milestone')} disabled={saving} placeholder="$45k in one month" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Years in Industry" type="number" min="0" value={form.years_in_industry} onChange={setField('years_in_industry')} disabled={saving} />
                <Input label="Clients Served" type="number" min="0" value={form.clients_served} onChange={setField('clients_served')} disabled={saving} />
              </div>
              <TagInput
                label="Designations"
                value={form.designations}
                onChange={(designations) => setForm((prev) => ({ ...prev, designations }))}
                placeholder="e.g. CLU, ChFC — press Enter to add"
                disabled={saving}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Awards</label>
                <div className="space-y-3">
                  {form.awards.map((award, index) => (
                    <div key={index} className="flex gap-2 items-start rounded-md border border-gray-200 p-3 bg-gray-50/50">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input
                          label="Award name"
                          value={award.award_name || ''}
                          onChange={(e) => {
                            const awards = form.awards.map((a, i) =>
                              i === index ? { ...a, award_name: e.target.value } : a,
                            );
                            updateAwards(awards);
                          }}
                          disabled={saving}
                        />
                        <Input
                          label="Year"
                          value={award.year || ''}
                          onChange={(e) => {
                            const awards = form.awards.map((a, i) =>
                              i === index ? { ...a, year: e.target.value } : a,
                            );
                            updateAwards(awards);
                          }}
                          disabled={saving}
                          placeholder="2024"
                        />
                      </div>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => updateAwards(form.awards.filter((_, i) => i !== index))}
                        className="p-2 text-gray-400 hover:text-red-600 mt-6"
                        aria-label="Remove award"
                      >
                        <SafeIcon icon={FiX} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => updateAwards([...form.awards, { award_name: '', year: '' }])}
                    className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark disabled:opacity-50"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    Add award
                  </button>
                </div>
              </div>
            </Card>
          </div>

          <div className={activeTab === 'booking' ? 'space-y-6' : 'hidden'}>
            <Card className="p-6 space-y-5">
              <SectionHeader
                icon={FiLink}
                title="Booking & CTA"
                subtitle="How prospects can book time with you."
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Calendar</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CALENDAR_MODE_OPTIONS.map((opt) => {
                    const selected = form.calendar_mode === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, calendar_mode: opt.id }))}
                        disabled={saving}
                        className={cn(
                          'text-left rounded-lg border p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60',
                          selected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
                        )}
                        aria-pressed={selected}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{opt.description}</p>
                          </div>
                          {selected && (
                            <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {form.calendar_mode === 'crm' && (
                <>
                  <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4 text-sm text-gray-700 space-y-2">
                    <div className="flex items-start gap-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <p>
                        Uses your connected Google Calendar and weekly availability from{' '}
                        <Link to="/settings/calendar" className="text-primary font-medium hover:underline">
                          Settings → Calendar
                        </Link>
                        . Prospects book at{' '}
                        <code className="text-xs bg-white/80 px-1 py-0.5 rounded border border-blue-100">
                          /book/{'{'}your-slug{'}'}
                        </code>
                        .
                      </p>
                    </div>
                    {googleConnected === false && (
                      <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        Google Calendar is not connected yet.{' '}
                        <Link to="/settings/calendar" className="font-medium underline">
                          Connect it in Settings → Calendar
                        </Link>{' '}
                        so public booking can show open slots.
                      </p>
                    )}
                  </div>
                  <Input label="Personal CTA Text" value={form.personal_cta_text} onChange={setField('personal_cta_text')} disabled={saving} placeholder="Book a call with me" />
                </>
              )}

              {form.calendar_mode === 'link' && (
                <>
                  <Input label="Personal Booking URL" value={form.personal_booking_url} onChange={setField('personal_booking_url')} disabled={saving} placeholder="https://calendly.com/…" />
                  <Input label="Personal CTA Text" value={form.personal_cta_text} onChange={setField('personal_cta_text')} disabled={saving} placeholder="Book a call with me" />
                </>
              )}

              {form.calendar_mode === 'embed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Embed code</label>
                  <textarea
                    value={form.calendar_embed_code}
                    onChange={(e) => setForm((prev) => ({ ...prev, calendar_embed_code: e.target.value }))}
                    disabled={saving}
                    rows={6}
                    placeholder={'<div class="calendly-inline-widget" …></div>\n<script src="…"></script>'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60"
                  />
                  <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Warning: raw HTML/script from this field will be rendered on your public site. Only paste
                    embed code from a trusted provider (Calendly, etc.).
                  </p>
                  <div className="mt-3">
                    <Input label="Personal CTA Text" value={form.personal_cta_text} onChange={setField('personal_cta_text')} disabled={saving} placeholder="Book a call with me" />
                  </div>
                </div>
              )}

              {form.calendar_mode === 'none' && (
                <p className="text-sm text-gray-500">
                  No booking CTA will appear on your public profile.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Dialer</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {DIALER_OPTIONS.map((opt) => {
                    const selected = form.preferred_dialer === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, preferred_dialer: opt.id }))}
                        disabled={saving}
                        className={cn(
                          'text-left rounded-lg border p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60',
                          selected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
                        )}
                        aria-pressed={selected}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{opt.description}</p>
                          </div>
                          {selected && (
                            <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Channel</label>
                <p className="text-sm text-gray-600 mb-3">
                  Pre-selects the business channel when you create a new lead.
                </p>
                <ChannelSelector
                  value={form.default_channel}
                  onChange={(default_channel) => setForm((prev) => ({ ...prev, default_channel }))}
                  allowEmpty
                  emptyLabel="No default"
                  disabled={saving}
                />
              </div>
            </Card>
          </div>

          <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-gray-50/95 backdrop-blur py-4 border-t border-gray-200 -mx-4 px-4 sm:mx-0 sm:px-0 sm:border-0 sm:bg-transparent sm:static">
            <Button
              type="button"
              variant="outline"
              disabled={!isDirty || saving}
              onClick={() => setForm(savedForm)}
            >
              Reset
            </Button>
            <Button type="button" disabled={!isDirty || saving} onClick={handleSave}>
              {saving ? (
                <>
                  <Spinner className="mr-2" />
                  Saving…
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="mr-2" />
                  Save profile
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
